import { RequestStatus } from '@weave/schema-gen-ts/dist/schemas/schedule/api/v2/api.pb';
import {
  BookingSubmission as ScheduleRequestV3,
  ReviewedStatus,
} from '@weave/schema-gen-ts/dist/schemas/schedule/v3/booking_submission.pb';
import { ScheduleQueries } from '@frontend/api-schedule';
import { SchedulerV3 } from '@frontend/api-schedule-v3';
import type { AiRequestDetails, ScheduleRequestListItem } from '../../../../../../types';

export const useGetScheduleRequest = (requestId: string, requestVersion: string, locationId: string) => {
  const { isScheduleV3FlagEnabled } = SchedulerV3.Helpers.useGetSchedulerV3FeatureFlagDetails();
  const shouldFetchV3 = requestVersion !== 'v2' && isScheduleV3FlagEnabled;

  const scheduleRequestQuery = ScheduleQueries.useGetScheduleRequestById(
    {
      id: requestId,
      locationId,
    },
    {
      enabled: !shouldFetchV3,
    }
  );

  const scheduleRequestQueryV3 = SchedulerV3.Queries.useGetBookingSubmissionQuery({
    request: {
      submissionId: requestId,
    },
    options: {
      enabled: shouldFetchV3 && !!requestId,
      select: (response) => {
        return response.bookingSubmission ? convertScheduleRequestV3ToV2(response.bookingSubmission) : undefined;
      },
    },
  });

  return shouldFetchV3 ? scheduleRequestQueryV3 : scheduleRequestQuery;
};

function tryParseJson<T>(jsonString?: string): T | undefined {
  try {
    return JSON.parse(jsonString ?? '');
  } catch (error) {
    return undefined;
  }
}

const parseAiRequestDetails = (aiRequestDetails?: string): AiRequestDetails | undefined => {
  if (!aiRequestDetails) return undefined;

  if (typeof aiRequestDetails === 'string') {
    return tryParseJson<AiRequestDetails>(aiRequestDetails);
  }

  return undefined;
};

type FormMetadataType = Record<string, string> & {
  gender?: string;
  isNewUser?: boolean;
};

export const convertScheduleRequestV3ToV2 = (scheduleRequest: ScheduleRequestV3): ScheduleRequestListItem => {
  const formMetadata = tryParseJson<FormMetadataType>(scheduleRequest.formMetadata);
  return {
    id: scheduleRequest.id,
    locationId: scheduleRequest.locationId,
    dateTime: scheduleRequest.createdAt,
    duration: scheduleRequest.slotDuration,
    // Note: schedulee and scheduler are the same in V2 (store patient info in it)
    schedulee: {
      firstName: scheduleRequest.firstName,
      lastName: scheduleRequest.lastName,
      phoneNumber: scheduleRequest.phoneNumber,
      address: scheduleRequest.addressInfo,
      birthDate: scheduleRequest.birthdate,
      email: scheduleRequest.email,
      gender: formMetadata?.gender ?? '',
      existingUser: !formMetadata?.isNewUser,
      insuranceInfo: scheduleRequest.formMetadata,
    },
    scheduler: {
      firstName: scheduleRequest.firstName,
      lastName: scheduleRequest.lastName,
      phoneNumber: scheduleRequest.phoneNumber,
      address: scheduleRequest.addressInfo,
      birthDate: scheduleRequest.birthdate,
      email: scheduleRequest.email,
      gender: formMetadata?.gender ?? '',
      existingUser: !formMetadata?.isNewUser,
      insuranceInfo: scheduleRequest.formMetadata,
    },
    note: scheduleRequest.note,
    appointmentTypeId: scheduleRequest.appointmentTypeId,
    bookableAppointmentTypeId: scheduleRequest.bookableAppointmentTypeId || '',
    practitionerId: scheduleRequest.providerId,
    reviewedBy: scheduleRequest.reviewedBy,
    requestedOpenings: (scheduleRequest.requestedSlots ?? []).map((slot) => ({ dateTime: slot })),
    bookingAmount: scheduleRequest.bookingDepositAmount ?? 0,
    paymentTransactionId: scheduleRequest.paymentTransactionId,
    aiRequestDetails: parseAiRequestDetails(scheduleRequest.bookingDetails),
    reviewedAt: scheduleRequest.updatedAt,
    status: mapReviewedStatusV3ToRequestStatusV2(scheduleRequest.reviewedStatus),
  };
};

const mapReviewedStatusV3ToRequestStatusV2 = (reviewedStatus?: ReviewedStatus): RequestStatus => {
  switch (reviewedStatus) {
    case ReviewedStatus.PENDING:
      return RequestStatus.PENDING;
    case ReviewedStatus.ACCEPTED:
      return RequestStatus.ACCEPTED;
    case ReviewedStatus.REJECTED:
      return RequestStatus.REJECTED;
    case ReviewedStatus.MESSAGED:
      return RequestStatus.MESSAGED;
    case ReviewedStatus.CALLED:
      return RequestStatus.CALLED;
    case ReviewedStatus.DISMISSED:
      return RequestStatus.DISMISSED;
    case ReviewedStatus.DONE:
      return RequestStatus.DONE;
    default:
      return RequestStatus.UNKNOWNSTATUS;
  }
};
